/**
 * FILE: packages/supabase/src/students/writes.ts
 * PURPOSE: Student write operations and export builders.
 * AGENT_CONTEXT: All writes call logActivity() with actor. Export builders use dynamic imports for heavy libs.
 */

import type { DbClient } from '../dbClient'
import { defaultDbClient } from '../dbClient'
export type { DbClient }
import { logActivity, type ActivityActor, withActivityActor } from '../internal/activity'
export type { ActivityActor } from '../internal/activity'
import {
  canonicalAttemptForStorage,
  cleanPhone,
  normaliseCourse,
  normaliseProgram,
  normalizeCheckoutPhone,
  validateStudentPhonePk,
  validatePhone,
} from '@fintree/utils'
import type { Database } from '../types'
import { getStudentByPhone } from './reads'
import { insertPhoneChangeLog } from './phoneChanges'
import { callInternalEdgeFunction } from '../internal/edgeFunctionAuth'
import type {
  CreateExistingStudentPayload,
  CreateExistingStudentResult,
  StudentsExportGroupBy,
  StudentExportRow,
  StudentsWorkbookMeta,
} from './dto'

type StudentRow = Database['public']['Tables']['students']['Row']
type StudentUpdate = Database['public']['Tables']['students']['Update']

export async function updateStudent(
  phone: string,
  updates: StudentUpdate,
  actor: ActivityActor,
  options?: { reason?: string; action?: string },
  client: DbClient = defaultDbClient
): Promise<StudentRow> {
  if (updates.phone != null && updates.phone !== phone) {
    throw new Error('Phone cannot be changed via field corrections. Use Change Phone Number (super admin).')
  }
  const patch: StudentUpdate = { ...updates }
  if (patch.course !== undefined && patch.course !== null) {
    const c = String(patch.course).trim()
    patch.course = c ? normaliseCourse(c) || c : null
  }
  if (patch.program !== undefined && patch.program !== null) {
    const p = String(patch.program).trim()
    patch.program = p ? normaliseProgram(p) : null
  }
  const previous = await getStudentByPhone(phone, client)
  if (!previous) throw new Error('Student not found or has been deleted.')

  if (patch.email !== undefined) {
    patch.email = patch.email === '' ? null : String(patch.email).trim().toLowerCase()
  }

  const prevExpBand = previous.exp_band ?? null
  const nextExpBand =
    patch.exp_band !== undefined ? (patch.exp_band === '' ? null : patch.exp_band) : undefined
  const expBandChanged = nextExpBand !== undefined && nextExpBand !== prevExpBand

  if (expBandChanged) {
    let placementLink: string | null = null
    if (nextExpBand) {
      const { data: linkRow } = await client
        .from('community_links')
        .select('link')
        .eq('type', 'placement')
        .eq('exp_band', nextExpBand)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle()
      placementLink = linkRow?.link ?? null
    }

    patch.pending_placement_refresh = {
      new_exp_band: nextExpBand,
      placement_link: placementLink,
      flagged_at: new Date().toISOString(),
      flagged_by: actor.userName ?? actor.userId,
    }

    const [{ data: profileRow }, { data: candidateRow }] = await Promise.all([
      client.from('student_profiles').select('phone').is('deleted_at', null).eq('phone', phone).maybeSingle(),
      client.from('candidates').select('phone').eq('phone', phone).maybeSingle(),
    ])

    const syncUpdates = []
    if (profileRow) {
      syncUpdates.push(client.from('student_profiles').update({ exp_band: nextExpBand }).eq('phone', phone))
    }
    if (candidateRow) {
      syncUpdates.push(client.from('candidates').update({ exp_band: nextExpBand }).eq('phone', phone))
    }
    if (syncUpdates.length) await Promise.all(syncUpdates)
  }

  const { data, error } = await client
    .from('students')
    .update(patch)
    .eq('phone', phone)
    .is('deleted_at', null)
    .select('*')
    .single()
  if (error) throw error

  const changes = options?.reason ? { ...data, reason: options.reason } : data
  await logActivity(
    withActivityActor(actor, {
      action: options?.action ?? 'student.update',
      entityType: 'students',
      entityId: phone,
      oldValues: previous ?? undefined,
      newValues: changes,
    }),
    client
  )

  if (expBandChanged) {
    await logActivity(
      withActivityActor(actor, {
        action: 'student.placement_refresh_flagged',
        entityType: 'students',
        entityId: phone,
        oldValues: { exp_band: prevExpBand },
        newValues: {
          exp_band: nextExpBand,
          pending_placement_refresh: patch.pending_placement_refresh,
        },
      }),
      client
    )
  }

  return data
}

export async function deleteStudent(
  phone: string,
  actor: ActivityActor,
  client: DbClient = defaultDbClient
): Promise<void> {
  const { error } = await client
    .from('students')
    .update({
      deleted_at: new Date().toISOString(),
      deleted_by: actor.userName,
    })
    .eq('phone', phone)

  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'DELETE_STUDENT',
      entityType: 'students',
      entityId: phone,
      newValues: { phone, deleted: true },
    }),
    client
  )
}

export async function restoreStudent(
  phone: string,
  actor: ActivityActor,
  client: DbClient = defaultDbClient
): Promise<void> {
  const { error } = await client
    .from('students')
    .update({ deleted_at: null, deleted_by: null })
    .eq('phone', phone)

  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'RESTORE_STUDENT',
      entityType: 'students',
      entityId: phone,
      newValues: { phone, deleted: false },
    }),
    client
  )
}

/**
 * Create a minimal student record for an existing student who needs portal access
 * but does not have a payment/enrollment yet. Used by the "Add Existing Student" flow.
 *
 * Invariants:
 * - Phone must not already exist (active or soft-deleted).
 * - No enrollment row is created.
 * - Onboarding email is suppressed via source = 'hub_existing_student'.
 * - A minimal student_profiles row and a candidates row are created.
 */
export async function createExistingStudentForPortal(
  payload: CreateExistingStudentPayload,
  actor: ActivityActor,
  client: DbClient = defaultDbClient
): Promise<CreateExistingStudentResult> {
  const rawPhone = payload.phone.trim()
  const normalized = normalizeCheckoutPhone({
    rawPhone,
    addressCountry: payload.country ?? null,
    addressCountryCode: payload.countryCode ?? null,
    currency: 'INR',
  })

  if (!normalized.ok) {
    throw new Error('Invalid phone number.')
  }

  const phone = normalized.phone
  if (!validateStudentPhonePk(phone)) {
    throw new Error('Phone must be 10–15 digits after normalization.')
  }

  const email = payload.email.trim().toLowerCase()
  if (!email || !email.includes('@')) {
    throw new Error('A valid email is required for portal login.')
  }

  // Block if phone already exists (active or deleted)
  const existingByPhone = await getStudentByPhone(phone, client)
  if (existingByPhone) {
    throw new Error(
      existingByPhone.deleted_at
        ? 'This phone belongs to a deleted student. Restore it first.'
        : 'This phone already belongs to an existing student.'
    )
  }

  const course = payload.course ? normaliseCourse(payload.course) || payload.course : null
  const program = payload.program ? normaliseProgram(payload.program) : null
  const attempt = payload.attempt ? canonicalAttemptForStorage(payload.attempt) || null : null

  const now = new Date().toISOString()
  const studentInsert = {
    phone,
    email,
    name: payload.name?.trim() || null,
    city: payload.city?.trim() || null,
    country: payload.country?.trim() || null,
    country_code: normalized.countryCode || payload.countryCode?.trim() || null,
    course,
    program,
    attempt,
    source: 'hub_existing_student' as const,
    can_edit_course_in_portal: true,
    valid_till: null,
    amount: null,
    amount_paid: null,
    currency: 'INR',
    gateway: null,
    enrollment_date: null,
    status: null,
    needs_review: false,
    created_at: now,
    updated_at: now,
  }

  const { data: student, error: insertError } = await client
    .from('students')
    .insert(studentInsert)
    .select('phone')
    .single()
  if (insertError) throw insertError

  // Create a minimal student_profiles row so portal gating works smoothly.
  // Omit updated_at; the student_profiles table does not have that column.
  const { error: profileError } = await client.from('student_profiles').insert({
    phone,
    name: studentInsert.name,
    course,
    exp_band: null,
    profile_completion_percent: 0,
    has_seen_tutorial: false,
    has_completed_wizard: false,
    created_at: now,
  })
  if (profileError) throw profileError

  // Auto-create a candidate record so placements staff can track them immediately
  const { error: candidateError } = await client.from('candidates').insert({
    phone,
    name: studentInsert.name,
    exp_band: null,
    placement_status: 'seeking',
    created_at: now,
    updated_at: now,
  })
  if (candidateError) throw candidateError

  await logActivity(
    withActivityActor(actor, {
      action: 'student.create',
      entityType: 'students',
      entityId: phone,
      newValues: {
        ...studentInsert,
        notes: payload.notes?.trim() || null,
      },
    }),
    client
  )

  return { phone: student.phone }
}

/**
 * Trigger the existing-student placements-portal access email.
 * Sends immediately; failures are swallowed so the Add Existing Student flow is not blocked.
 */
export async function sendExistingStudentPortalEmail(phone: string): Promise<void> {
  try {
    const response = await callInternalEdgeFunction('send-existing-student-portal-email', {
      student_phone: phone,
    })
    if (!response.ok) {
      console.error('[sendExistingStudentPortalEmail] failed:', response.status, await response.text())
    }
  } catch (err) {
    console.error('[sendExistingStudentPortalEmail] error:', err)
  }
}

/**
 * Re-point primary key `phone` across Hub-related tables. Requires DB function `merge_student_phone`.
 */
export async function mergeStudentPhone(
  oldPhone: string,
  newPhone: string,
  actor: ActivityActor,
  options?: { reason?: string },
  client: DbClient = defaultDbClient
): Promise<void> {
  const oldP = cleanPhone(oldPhone) || oldPhone.trim()
  const newP = cleanPhone(newPhone)
  if (!validatePhone(newPhone)) {
    throw new Error('New phone must be a valid 10-digit Indian mobile.')
  }
  if (oldP === newP) {
    throw new Error('New phone is the same as the current phone.')
  }

  const conflict = await getStudentByPhone(newP, client)
  if (conflict) {
    throw new Error(`This phone already belongs to ${conflict.name ?? 'another student'}. Cannot reassign.`)
  }
  const { data: oldStudent } = await client
    .from('students')
    .select('phone, email, deleted_at')
    .eq('phone', oldP)
    .maybeSingle()
  if (!oldStudent || oldStudent.deleted_at) {
    throw new Error('Original student not found or has been deleted.')
  }

  const { error } = await client.rpc('merge_student_phone', {
    p_old_phone: oldP,
    p_new_phone: newP,
  })
  if (error) {
    if (error.message?.includes('merge_student_phone') || error.code === '42883') {
      throw new Error(
        'Database function merge_student_phone is missing. Run docs/archive/sql-audits/mergeStudentPhone.sql in Supabase SQL editor.'
      )
    }
    throw error
  }

  await logActivity(
    withActivityActor(actor, {
      action: 'CHANGE_PHONE',
      entityType: 'students',
      entityId: newP,
      oldValues: { phone: oldP, reason: options?.reason },
      newValues: { phone: newP },
    }),
    client
  )

  await insertPhoneChangeLog(
    {
      oldPhone: oldP,
      newPhone: newP,
      changeType: 'merge',
      actor,
      reason: options?.reason,
      details: {},
    },
    { swallowErrors: true },
    client
  )
}

export async function extendStudentAccess(
  phone: string,
  durationAdded: number,
  actor: ActivityActor,
  client: DbClient = defaultDbClient
): Promise<{ newValidTill: string; extensionCount: number }> {
  const previous = await getStudentByPhone(phone, client)
  if (!previous) throw new Error('Student not found or has been deleted.')

  const currentValidTill = previous.valid_till ? new Date(previous.valid_till) : new Date()
  const newValidTill = new Date(currentValidTill)
  newValidTill.setMonth(newValidTill.getMonth() + durationAdded)
  const newValidTillStr = newValidTill.toISOString().slice(0, 10)
  const lastExtendedAt = new Date().toISOString()
  const extensionCount = (previous.extension_count ?? 0) + 1

  const { error } = await client
    .from('students')
    .update({
      valid_till: newValidTillStr,
      extension_count: extensionCount,
      last_extended_at: lastExtendedAt,
    })
    .eq('phone', phone)
    .is('deleted_at', null)

  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'student.extend',
      entityType: 'students',
      entityId: phone,
      oldValues: {
        valid_till: previous.valid_till,
        extension_count: previous.extension_count,
      },
      newValues: {
        valid_till: newValidTillStr,
        extension_count: extensionCount,
        last_extended_at: lastExtendedAt,
      },
    }),
    client
  )

  return { newValidTill: newValidTillStr, extensionCount }
}

export async function updateStudentStatus(
  phone: string,
  status: string,
  actor: ActivityActor,
  client: DbClient = defaultDbClient
): Promise<{ newStatus: string }> {
  const previous = await getStudentByPhone(phone, client)
  if (!previous) throw new Error('Student not found or has been deleted.')

  const { error } = await client.from('students').update({ status }).eq('phone', phone).is('deleted_at', null)

  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'student.status_update',
      entityType: 'students',
      entityId: phone,
      oldValues: { status: previous.status },
      newValues: { status },
    }),
    client
  )

  return { newStatus: status }
}

export async function updateStudentExpBand(
  phone: string,
  expBand: string,
  actor: ActivityActor,
  client: DbClient = defaultDbClient
): Promise<{ newExpBand: string }> {
  const previous = await getStudentByPhone(phone, client)
  if (!previous) throw new Error('Student not found or has been deleted.')

  const { error } = await client.from('students').update({ exp_band: expBand }).eq('phone', phone).is('deleted_at', null)

  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'student.exp_band_update',
      entityType: 'students',
      entityId: phone,
      oldValues: { exp_band: previous.exp_band },
      newValues: { exp_band: expBand },
    }),
    client
  )

  return { newExpBand: expBand }
}

// ---------------------------------------------------------------------------
// Export helpers (moved from services/studentExport.ts)
// ---------------------------------------------------------------------------

import { getCourseLabel, getProgramLabel } from '@fintree/utils'

const EXCEL_MAX_SHEETS = 255
const SHEET_NAME_MAX_LEN = 31
const INVALID_SHEET_CHARS = /[\\/*?:[\]]/g

export function mapStudentToExportRow(row: StudentRow): StudentExportRow {
  return {
    Phone: row.phone,
    Name: row.name ?? '',
    Email: row.email ?? '',
    Course: row.course ? getCourseLabel(row.course) : '',
    Package: row.program ? getProgramLabel(row.program) : '',
    Attempt: row.attempt ?? '',
    'Valid Till': row.valid_till ?? '',
    'Exp Band': row.exp_band ?? '',
    City: row.city ?? '',
    Country: row.country ?? '',
    Status: row.status ?? '',
    'Enrollment Date': row.enrollment_date ?? '',
    Amount: row.amount != null ? String(row.amount) : '',
    Source: row.source ?? '',
  }
}

export function getStudentGroupKey(row: StudentRow, groupBy: StudentsExportGroupBy): string {
  switch (groupBy) {
    case 'city':
      return row.city?.trim() ? row.city.trim() : 'Not_Set'
    case 'course':
      return row.course ? getCourseLabel(row.course) : 'Unknown'
    case 'program':
      return row.program ? getProgramLabel(row.program) : 'Unknown'
    case 'attempt':
      return row.attempt?.trim() ? row.attempt.trim() : 'Not_Set'
    case 'status':
      return row.status?.trim() ? row.status.trim() : 'Not_Set'
    default:
      return 'All'
  }
}

export function sanitizeSheetName(name: string, usedNames: Set<string>): string {
  let base = name.replace(INVALID_SHEET_CHARS, '_').trim() || 'Sheet'
  if (base.length > SHEET_NAME_MAX_LEN) base = base.slice(0, SHEET_NAME_MAX_LEN)

  let candidate = base
  let suffix = 2
  while (usedNames.has(candidate)) {
    const tail = ` (${suffix})`
    candidate = `${base.slice(0, Math.max(SHEET_NAME_MAX_LEN - tail.length, 1))}${tail}`
    suffix += 1
  }
  usedNames.add(candidate)
  return candidate
}

function sortStudentsForExport(rows: StudentRow[], groupBy: StudentsExportGroupBy): StudentRow[] {
  return [...rows].sort((a, b) => {
    const groupCompare = getStudentGroupKey(a, groupBy).localeCompare(getStudentGroupKey(b, groupBy))
    if (groupCompare !== 0) return groupCompare
    return (a.name ?? a.phone).localeCompare(b.name ?? b.phone)
  })
}

function groupStudents(rows: StudentRow[], groupBy: StudentsExportGroupBy): Map<string, StudentRow[]> {
  const groups = new Map<string, StudentRow[]>()
  for (const row of rows) {
    const key = getStudentGroupKey(row, groupBy)
    const bucket = groups.get(key)
    if (bucket) bucket.push(row)
    else groups.set(key, [row])
  }
  return groups
}

const EXPORT_HEADERS: Array<keyof StudentExportRow> = [
  'Phone', 'Name', 'Email', 'Course', 'Package', 'Attempt',
  'Valid Till', 'Exp Band', 'City', 'Country', 'Status',
  'Enrollment Date', 'Amount', 'Source',
]

export function buildStudentsCsv(rows: StudentRow[]): string {
  const headers = EXPORT_HEADERS
  const body = rows.map((row) => {
    const mapped = mapStudentToExportRow(row)
    return headers.map((header) => mapped[header])
  })

  return [headers, ...body]
    .map((cells) => cells.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(','))
    .join('\n')
}

export async function buildStudentsWorkbook(
  rows: StudentRow[],
  groupBy: StudentsExportGroupBy
): Promise<{ blob: Blob; meta: StudentsWorkbookMeta }> {
  const XLSX = await import('xlsx')
  const sorted = sortStudentsForExport(rows, groupBy)
  const wb = XLSX.utils.book_new()
  const usedNames = new Set<string>()
  const mergedIntoOther: string[] = []

  if (groupBy === 'none') {
    const summaryRows = [{ Group: 'All Students', Count: sorted.length }]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), sanitizeSheetName('Summary', usedNames))
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(sorted.map(mapStudentToExportRow)),
      sanitizeSheetName('All_Students', usedNames)
    )
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' })
    return {
      blob: new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      meta: { sheetCount: 2, groupCount: 1, mergedIntoOther: [] },
    }
  }

  const groups = groupStudents(sorted, groupBy)
  const groupEntries = [...groups.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))

  const maxDataSheets = EXCEL_MAX_SHEETS - 1
  let entriesForSheets = groupEntries
  if (groupEntries.length > maxDataSheets) {
    const primaryCount = maxDataSheets - 1
    const primary = groupEntries.slice(0, primaryCount)
    const overflow = groupEntries.slice(primaryCount)
    mergedIntoOther.push(...overflow.map(([key]) => key))
    const otherRows = overflow.flatMap(([, groupRows]) => groupRows)
    entriesForSheets = [...primary, ['Other', otherRows] as [string, StudentRow[]]]
  }

  const summaryRows = groupEntries.map(([group, groupRows]) => ({
    Group: group,
    Count: groupRows.length,
    ...(mergedIntoOther.includes(group) ? { Note: 'Merged into Other sheet' } : {}),
  }))
  if (mergedIntoOther.length) {
    summaryRows.push({ Group: 'Other (overflow)', Count: mergedIntoOther.reduce((sum, key) => sum + (groups.get(key)?.length ?? 0), 0), Note: mergedIntoOther.join(', ') })
  }

  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), sanitizeSheetName('Summary', usedNames))

  for (const [group, groupRows] of entriesForSheets) {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(groupRows.map(mapStudentToExportRow)),
      sanitizeSheetName(group, usedNames)
    )
  }

  const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' })
  return {
    blob: new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    meta: {
      sheetCount: wb.SheetNames.length,
      groupCount: groupEntries.length,
      mergedIntoOther,
    },
  }
}

export async function consolidateStudentPhone(
  oldPhone: string,
  newPhone: string,
  actor: ActivityActor,
  options?: { reason?: string },
  client: DbClient = defaultDbClient
): Promise<Record<string, unknown>> {
  if (!validateStudentPhonePk(oldPhone) || !validateStudentPhonePk(newPhone)) {
    throw new Error('Invalid phone number')
  }
  const { data, error } = await client.rpc('consolidate_student_phone', {
    p_old_phone: oldPhone,
    p_new_phone: newPhone,
  })
  if (error) throw new Error(error.message)
  const result = data as Record<string, unknown> | null
  if (!result) throw new Error('Consolidation returned no result')
  if (result.success === false) {
    throw new Error(String(result.error ?? 'Consolidation failed'))
  }

  await logActivity(
    withActivityActor(actor, {
      action: 'CONSOLIDATE_PHONE',
      entityType: 'students',
      entityId: newPhone,
      oldValues: { phone: oldPhone, reason: options?.reason },
      newValues: { phone: newPhone, summary: result },
    }),
    client
  )

  await insertPhoneChangeLog(
    {
      oldPhone,
      newPhone,
      changeType: 'consolidate',
      actor,
      reason: options?.reason,
      details: result,
    },
    { swallowErrors: true },
    client
  )

  return result
}

export async function changeStudentPhone(
  oldPhone: string,
  newPhone: string,
  actor: ActivityActor,
  options?: { reason?: string },
  client: DbClient = defaultDbClient
): Promise<{
  mode: 'merge' | 'consolidate'
  oldPhone: string
  newPhone: string
  summary?: Record<string, unknown>
}> {
  const oldP = cleanPhone(oldPhone) || oldPhone.trim()
  const newP = cleanPhone(newPhone)
  if (!validateStudentPhonePk(newP)) {
    throw new Error('New phone must be a valid phone number.')
  }
  if (oldP === newP) {
    throw new Error('New phone is the same as the current phone.')
  }

  const target = await getStudentByPhone(newP, client)
  if (target) {
    const summary = await consolidateStudentPhone(oldP, newP, actor, options, client)
    return { mode: 'consolidate', oldPhone: oldP, newPhone: newP, summary }
  }

  await mergeStudentPhone(oldP, newP, actor, options, client)
  return { mode: 'merge', oldPhone: oldP, newPhone: newP }
}
